import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PaymentIcon from '@mui/icons-material/Payment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import config from '../../../config';

const STATUS_COLOR = {
  Active:    { bg: '#fef3c7', color: '#92400e' },
  Closed:    { bg: '#dcfce7', color: '#166534' },
  Cancelled: { bg: '#fee2e2', color: '#991b1b' },
};

// HR Advance Salary — issue advance + record recovery installments.
// PendingAmount is auto-derived (AdvanceAmount − RecoveredAmount). When
// cumulative recovery hits the principal the row auto-closes.
export default function AdvanceSalary() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;

  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empFilter, setEmpFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');

  const today = new Date().toISOString().slice(0, 10);

  // New advance
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    employeeId: '',
    advanceDate: today,
    advanceAmount: '',
    recoveryMonths: 1,
    reason: '',
  });
  const [saving, setSaving] = useState(false);

  // Recovery / ledger view
  const [recOpen, setRecOpen] = useState(false);
  const [recTarget, setRecTarget] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [recForm, setRecForm] = useState({ recoveredAt: today, amount: '' });

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const loadEmployees = async () => {
    const res = await fetch(
      `${config.API_BASE_URL}/Hr/Employees/${companyId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) setEmployees(await res.json());
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (empFilter) qs.append('employeeId', empFilter);
      if (statusFilter) qs.append('status', statusFilter);
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Advances/${companyId}?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setRows(await res.json());
    } catch (e) {
      toast('Failed to load advances', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, empFilter, statusFilter]);

  const totals = useMemo(() => {
    return {
      count: rows.length,
      issued: rows.reduce((a, r) => a + Number(r.advanceAmount || 0), 0),
      recovered: rows.reduce((a, r) => a + Number(r.recoveredAmount || 0), 0),
      pending: rows.reduce((a, r) => a + Number(r.pendingAmount || 0), 0),
    };
  }, [rows]);

  const save = async () => {
    if (!form.employeeId || !form.advanceAmount) {
      toast('Employee + amount required', 'warning');
      return;
    }
    setSaving(true);
    try {
      const body = {
        Id: null,
        CompanyId: companyId,
        EmployeeId: Number(form.employeeId),
        AdvanceDate: form.advanceDate,
        AdvanceAmount: Number(form.advanceAmount),
        RecoveryMonths: Number(form.recoveryMonths) || 1,
        Reason: form.reason || null,
        UserId: user?.id || null,
      };
      const res = await fetch(`${config.API_BASE_URL}/Hr/Advance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast('Save failed', 'error');
        return;
      }
      toast('Advance issued');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openRecovery = async (r) => {
    setRecTarget(r);
    setRecForm({ recoveredAt: today, amount: '' });
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/AdvanceRecovery/${r.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) setLedger(await res.json());
    } catch (e) {
      setLedger([]);
    }
    setRecOpen(true);
  };

  const saveRecovery = async () => {
    if (!recTarget || !recForm.amount) {
      toast('Enter amount', 'warning');
      return;
    }
    const body = {
      CompanyId: companyId,
      AdvanceId: recTarget.id,
      RecoveredAt: recForm.recoveredAt,
      Amount: Number(recForm.amount),
      UserId: user?.id || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/AdvanceRecovery`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast('Save failed', 'error');
      return;
    }
    toast('Recovery recorded');
    // refresh ledger + main list
    openRecovery(recTarget);
    load();
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='center'
        sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#6366f1',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <PaymentIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Advance Salary
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
              HR · {totals.count} advance(s) · ₹{totals.issued.toLocaleString('en-IN')} issued
            </Typography>
          </Box>
        </Stack>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => {
            setForm({
              employeeId: '',
              advanceDate: today,
              advanceAmount: '',
              recoveryMonths: 1,
              reason: '',
            });
            setDialogOpen(true);
          }}
          sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
          New Advance
        </Button>
      </Stack>

      {/* KPI cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          ['Total issued', totals.issued, '#6366f1'],
          ['Recovered', totals.recovered, '#16a34a'],
          ['Pending', totals.pending, '#dc2626'],
        ].map(([label, val, color]) => (
          <Grid item xs={12} sm={4} key={label}>
            <Card sx={{ borderLeft: `4px solid ${color}`, p: 1.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                {label}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                ₹{Number(val).toLocaleString('en-IN')}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2 }}>
        <TextField
          select
          size='small'
          label='Employee'
          value={empFilter}
          onChange={(e) => setEmpFilter(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 220 }}>
          <MenuItem value=''>All</MenuItem>
          {employees.map((e) => (
            <MenuItem key={e.id} value={e.id}>
              {e.employeeName} ({e.employeeCode})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label='Status'
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 160 }}>
          <MenuItem value=''>All</MenuItem>
          <MenuItem value='Active'>Active</MenuItem>
          <MenuItem value='Closed'>Closed</MenuItem>
          <MenuItem value='Cancelled'>Cancelled</MenuItem>
        </TextField>
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Recovered
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Pending
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Months</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          No advances.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => {
                    const sc = STATUS_COLOR[r.status] || STATUS_COLOR.Active;
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          {new Date(r.advanceDate).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {r.employeeName}
                          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                            {r.employeeCode}
                          </Typography>
                        </TableCell>
                        <TableCell align='right' sx={{ fontWeight: 700 }}>
                          ₹{Number(r.advanceAmount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell align='right' sx={{ color: '#16a34a' }}>
                          ₹{Number(r.recoveredAmount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell align='right' sx={{ color: '#dc2626' }}>
                          ₹{Number(r.pendingAmount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell>{r.recoveryMonths}</TableCell>
                        <TableCell sx={{ color: '#475569' }}>
                          {r.reason || '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            label={r.status}
                            sx={{
                              bgcolor: sc.bg,
                              color: sc.color,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Tooltip title='Recovery ledger / add recovery'>
                            <IconButton
                              size='small'
                              onClick={() => openRecovery(r)}>
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add advance dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Advance</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                size='small'
                fullWidth
                label='Employee *'
                value={form.employeeId}
                onChange={(e) =>
                  setForm({ ...form, employeeId: e.target.value })
                }>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.employeeName} ({e.employeeCode})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='date'
                size='small'
                fullWidth
                label='Date *'
                InputLabelProps={{ shrink: true }}
                value={form.advanceDate}
                onChange={(e) =>
                  setForm({ ...form, advanceDate: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Amount *'
                inputProps={{ min: 0 }}
                value={form.advanceAmount}
                onChange={(e) =>
                  setForm({ ...form, advanceAmount: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='number'
                size='small'
                fullWidth
                label='Recovery Months'
                inputProps={{ min: 1 }}
                value={form.recoveryMonths}
                onChange={(e) =>
                  setForm({ ...form, recoveryMonths: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                fullWidth
                multiline
                rows={2}
                label='Reason'
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={save}
            disabled={saving}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
            {saving ? 'Saving…' : 'Issue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recovery ledger dialog */}
      <Dialog
        open={recOpen}
        onClose={() => setRecOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Recovery Ledger
          {recTarget && (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>
              {recTarget.employeeName} · ₹
              {Number(recTarget.advanceAmount).toLocaleString('en-IN')} advance ·
              Pending: ₹
              {Number(recTarget.pendingAmount).toLocaleString('en-IN')}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {recTarget?.status === 'Active' && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mb: 2 }}>
              <TextField
                type='date'
                size='small'
                label='Recovered At *'
                InputLabelProps={{ shrink: true }}
                value={recForm.recoveredAt}
                onChange={(e) =>
                  setRecForm({ ...recForm, recoveredAt: e.target.value })
                }
              />
              <TextField
                type='number'
                size='small'
                label='Amount *'
                value={recForm.amount}
                onChange={(e) =>
                  setRecForm({ ...recForm, amount: e.target.value })
                }
                inputProps={{ min: 0 }}
              />
              <Button variant='contained' onClick={saveRecovery}>
                Add Recovery
              </Button>
            </Stack>
          )}
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payroll #</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledger.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align='center' sx={{ py: 3 }}>
                      <Typography sx={{ color: '#94a3b8' }}>
                        No recoveries yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {ledger.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>
                      {new Date(l.recoveredAt).toLocaleDateString('en-IN')}
                    </TableCell>
                    <TableCell>
                      ₹{Number(l.amount).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell>{l.payrollId || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
